import { useState, useMemo, useEffect } from 'react';
import { useCharacters, type CharacterCategory } from '@/api';
import {
  SearchBar,
  CategoryDropdown,
  FiltersBar,
  Pagination,
  EmptyState,
  LoadingState,
  ErrorState,
  CharactersGrid,
} from '@/app/components/explore';

const CATEGORY_MAP: Record<string, string> = {
  'screen-legends': 'Screen Legends',
  'toons': 'Toons',
  'irl-vibes': 'IRL Vibes',
  'memeverse': 'Memeverse',
  'original-chaos': 'Original Chaos',
};

const categoryFilters = ['All', ...Object.values(CATEGORY_MAP)];

export function BotExplorePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [page, setPage] = useState(1);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build API params
  const apiParams = useMemo(() => {
    const params: { search?: string; category?: CharacterCategory; page: number; limit: number } = {
      page,
      limit: 12,
    };

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    if (categoryFilter !== 'All') {
      const categoryKey = Object.entries(CATEGORY_MAP).find(
        ([_, value]) => value === categoryFilter
      )?.[0] as CharacterCategory | undefined;

      if (categoryKey) {
        params.category = categoryKey;
      }
    }

    return params;
  }, [debouncedSearch, categoryFilter, page]);

  const { data, isLoading, error } = useCharacters(apiParams);

  const characters = data?.data.characters ?? [];
  const pagination = data?.data.pagination;

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryFilter]);

  const clearAllFilters = () => {
    setCategoryFilter('All');
    setSearchQuery('');
    setDebouncedSearch('');
    setPage(1);
  };

  const activeFiltersCount = (categoryFilter !== 'All' ? 1 : 0) + (debouncedSearch.trim() ? 1 : 0);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 min-h-screen">
      {/* Header */}
      <div className="mb-12">
        <h1 className="font-['Poppins',sans-serif] text-4xl font-semibold text-white mb-3">
          Explore AI Characters
        </h1>
        <p className="text-lg text-[#9595a5]">
          Discover {pagination?.total ?? 0} blockchain-powered AI personalities
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-8 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name, category, description..."
          />
          <CategoryDropdown
            categories={categoryFilters}
            selected={categoryFilter}
            onSelect={setCategoryFilter}
          />
        </div>

        <FiltersBar
          activeFiltersCount={activeFiltersCount}
          resultsCount={characters.length}
          currentPage={page}
          totalPages={pagination?.totalPages ?? 1}
          onClearFilters={clearAllFilters}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} />
      ) : characters.length > 0 ? (
        <>
          <CharactersGrid characters={characters} />
          <Pagination
            currentPage={page}
            totalPages={pagination?.totalPages ?? 1}
            hasMore={pagination?.hasMore ?? false}
            onPageChange={handlePageChange}
          />
        </>
      ) : (
        <EmptyState searchQuery={debouncedSearch} onClearFilters={clearAllFilters} />
      )}
    </div>
  );
}